import L from "leaflet";
import { declutterLabels, LabelNode, Rect, Viewport } from "./declutter";

// Custom map overlay for the fleet: instead of marker clustering, EVERY vehicle
// is always drawn and its name label is decluttered in pixel space so labels
// never overlap. Markers keep the production online/offline semantics
// (blue / grey). An optional localization-confidence halo (true-meter radius)
// can be toggled on.
//
// One <svg> in a dedicated pane; paint-ordered <g> groups give the z-stack
// halos -> leaders -> markers -> labels. No L.marker / L.tooltip — those don't
// declutter.

const SVGNS = "http://www.w3.org/2000/svg";
const PANE = "fleet-labels";
const PADDING = 0.1;

const ONLINE = "#0394e8";
const OFFLINE = "#6b7280";
// Hover/selection highlight — a warm accent distinct from the online/offline
// palette, so "you're about to open this one" reads at a glance.
const HOVER = "#ffab00";

// Label anchor offset from the marker (px): up and to the right.
const ANCHOR_DX = 16;
const ANCHOR_DY = -16;
const LABEL_H = 20;
const CHAR_W = 7.5; // ~bold 13px sans advance
const LABEL_PAD_X = 9;

export interface LabelVehicle {
  id: string;
  position: L.LatLngExpression;
  label: string;
  status: "online" | "offline";
  action: () => void;
  // Localization 1-sigma horizontal error in meters; drives the halo radius.
  // null/undefined => no confidence data for this vehicle.
  confidenceRadiusM?: number | null;
}

export interface FleetLabelLayerOptions {
  showConfidence?: boolean;
}

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number>,
): SVGElementTagNameMap[K] {
  const e = document.createElementNS(SVGNS, tag);
  for (const k in attrs) e.setAttribute(k, String(attrs[k]));
  return e;
}

const labelWidth = (text: string) => text.length * CHAR_W + LABEL_PAD_X * 2;

// EPSG:3857 ground resolution -> pixels for a real-meter radius (auto-scales
// with zoom, like an L.circle, with no fudge factor).
function metersToPixels(meters: number, lat: number, zoom: number): number {
  const metersPerPixel =
    (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);
  return meters / metersPerPixel;
}

export class FleetLabelLayer extends L.Layer {
  private _svg!: SVGSVGElement;
  private _gHalo!: SVGGElement;
  private _gLeader!: SVGGElement;
  private _gMarker!: SVGGElement;
  private _gLabel!: SVGGElement;

  private _vehicles: LabelVehicle[] = [];
  private _showConfidence: boolean;
  // Vehicle whose label/marker is currently hovered, so the glow survives the
  // periodic redraws (data polls, map moves) that rebuild the SVG.
  private _hoveredId: string | null = null;

  // Persisted solved label centers (layer points) keyed by vehicle id so labels
  // glide between frames instead of snapping; pruned when a vehicle leaves.
  private _labelPos = new Map<string, { cx: number; cy: number }>();
  private _frame: number | null = null;
  private _center!: L.LatLng;
  private _zoom!: number;

  constructor(options: FleetLabelLayerOptions = {}) {
    super();
    this._showConfidence = options.showConfidence ?? false;
  }

  setVehicles(vehicles: LabelVehicle[]): void {
    this._vehicles = vehicles;
    this._scheduleRedraw();
  }

  setShowConfidence(show: boolean): void {
    this._showConfidence = show;
    this._scheduleRedraw();
  }

  onAdd(map: L.Map): this {
    this._map = map;
    if (!map.getPane(PANE)) {
      const pane = map.createPane(PANE);
      pane.style.zIndex = "650"; // above markerPane (600)
      pane.style.pointerEvents = "none";
    }
    // leaflet-zoom-animated lets Leaflet's CSS zoom transition tween our svg's
    // transform in lockstep with the tiles during animated zoom.
    this._svg = svgEl("svg", {
      class: "fleet-label-svg leaflet-zoom-animated",
    });
    this._svg.style.pointerEvents = "none";
    // The svg box is the viewport plus PADDING and is re-fitted only when a
    // view change settles. A browser clips an inline svg to its box, so during
    // a drag, drag inertia, a pinch or a zoom animation every dot outside the
    // stale box would paint nowhere until moveend / zoomend. Let the content
    // overflow instead: every vehicle's dot and halo are drawn whatever their
    // position, so they paint the moment they enter the frame.
    this._svg.style.overflow = "visible";
    this._gHalo = svgEl("g", {});
    this._gLeader = svgEl("g", {});
    this._gMarker = svgEl("g", {});
    this._gLabel = svgEl("g", {});
    this._svg.append(this._gHalo, this._gLeader, this._gMarker, this._gLabel);
    map.getPane(PANE)!.appendChild(this._svg);

    this._center = map.getCenter();
    this._zoom = map.getZoom();
    // Full redraw (re-project + re-declutter) on every `move` as well as on
    // settled view changes. Leaflet fires `move` once per frame during a drag,
    // drag inertia, a keyboard pan and flyTo, so labels follow the pan live and
    // a vehicle that enters the frame gets its label before moveend. The fleet
    // is tens of vehicles, so a redraw costs well under a frame. A zoom
    // animation fires no `move`: the existing svg is transformed in lockstep
    // with the tiles instead (below).
    map.on("move moveend zoomend viewreset resize", this._onViewChange, this);
    map.on("zoom", this._onZoom, this);
    map.on("zoomanim", this._onZoomAnim, this);
    this._update();
    return this;
  }

  onRemove(map: L.Map): this {
    if (this._frame !== null) cancelAnimationFrame(this._frame);
    this._frame = null;
    map.off("move moveend zoomend viewreset resize", this._onViewChange, this);
    map.off("zoom", this._onZoom, this);
    map.off("zoomanim", this._onZoomAnim, this);
    this._svg.remove();
    return this;
  }

  // View changes (move / moveend / zoomend / viewreset / resize) redraw
  // IMMEDIATELY — synchronously, like L.Renderer — so the decluttered labels
  // are correct the instant the map moves or a zoom finishes, not a frame (or
  // paint) later.
  private _onViewChange = (): void => this._redrawNow();

  private _redrawNow(): void {
    if (this._frame !== null) {
      cancelAnimationFrame(this._frame);
      this._frame = null;
    }
    this._update();
  }

  // Coalesced redraw for data-driven updates (setVehicles / setShowConfidence),
  // where batching multiple calls into one frame is fine.
  private _scheduleRedraw(): void {
    if (this._frame !== null || !this._map) return;
    this._frame = requestAnimationFrame(() => {
      this._frame = null;
      this._update();
    });
  }

  // Continuous (non-animated) zoom steps — e.g. wheel/pinch: transform only.
  private _onZoom = (): void =>
    this._updateTransform(this._map.getCenter(), this._map.getZoom());

  // Animated zoom: set the target transform once; the leaflet-zoom-animated
  // class lets Leaflet's CSS transition tween it alongside the tiles.
  private _onZoomAnim = (e: L.ZoomAnimEvent): void =>
    this._updateTransform(e.center, e.zoom);

  // Translate + scale the existing svg to match (center, zoom) without a
  // redraw — mirrors L.Renderer so the overlay tracks the map smoothly.
  private _updateTransform(center: L.LatLng, zoom: number): void {
    const map = this._map;
    const scale = map.getZoomScale(zoom, this._zoom);
    const position = L.DomUtil.getPosition(this._svg as unknown as HTMLElement);
    const viewHalf = map.getSize().multiplyBy(0.5 + PADDING);
    const currentCenterPoint = map.project(this._center, zoom);
    const destCenterPoint = map.project(center, zoom);
    const centerOffset = destCenterPoint.subtract(currentCenterPoint);
    const topLeftOffset = viewHalf
      .multiplyBy(-scale)
      .add(position)
      .add(viewHalf)
      .subtract(centerOffset);
    L.DomUtil.setTransform(
      this._svg as unknown as HTMLElement,
      topLeftOffset,
      scale,
    );
  }

  private _update(): void {
    const map = this._map;
    if (!map) return;
    this._center = map.getCenter();
    this._zoom = map.getZoom();

    // Everything is in Leaflet "layer points" (what latLngToLayerPoint returns)
    // — the same scheme L.SVG uses. getPixelBounds() is a different space.
    const size = map.getSize();
    const min = map
      .containerPointToLayerPoint(size.multiplyBy(-PADDING))
      .round();
    const max = min.add(size.multiplyBy(1 + 2 * PADDING)).round();
    const w = max.x - min.x;
    const h = max.y - min.y;
    L.DomUtil.setPosition(this._svg as unknown as HTMLElement, min);
    this._svg.setAttribute("width", String(w));
    this._svg.setAttribute("height", String(h));
    this._svg.setAttribute("viewBox", `${min.x} ${min.y} ${w} ${h}`);
    this._draw();
  }

  // Leaflet control corners (zoom, fullscreen, recenter, confidence toggle) as
  // obstacle rects so labels don't hide under the on-map UI.
  private _controlObstacles(originX: number, originY: number): Rect[] {
    const container = this._map.getContainer();
    const cb = container.getBoundingClientRect();
    const rects: Rect[] = [];
    const sel =
      ".leaflet-top.leaflet-left, .leaflet-top.leaflet-right, " +
      ".leaflet-bottom.leaflet-right, .confidence-toggle";
    container.querySelectorAll(sel).forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return;
      rects.push({
        cx: r.left + r.width / 2 - cb.left + originX,
        cy: r.top + r.height / 2 - cb.top + originY,
        hw: r.width / 2 + 6,
        hh: r.height / 2 + 6,
      });
    });
    return rects;
  }

  private _draw(): void {
    const map = this._map;
    const size = map.getSize();
    const originLayer = map.containerPointToLayerPoint([0, 0]);
    const vps: Viewport = {
      minX: originLayer.x,
      minY: originLayer.y,
      maxX: originLayer.x + size.x,
      maxY: originLayer.y + size.y,
    };
    const obstacles = this._controlObstacles(originLayer.x, originLayer.y);

    const markers = this._vehicles.map((v) => {
      const p = map.latLngToLayerPoint(L.latLng(v.position));
      return {
        v,
        x: p.x,
        y: p.y,
        color: v.status === "online" ? ONLINE : OFFLINE,
      };
    });

    // Only label vehicles whose ACTUAL position is on-screen. Off-screen ones
    // would otherwise have their label clamped to the viewport edge, littering
    // the map with names for tractors you can't see.
    const onScreen = (x: number, y: number) =>
      x >= vps.minX && x <= vps.maxX && y >= vps.minY && y <= vps.maxY;

    // Seed label nodes from persisted positions (or the anchor for new ids).
    const labels: LabelNode[] = markers
      .filter((m) => onScreen(m.x, m.y))
      .map((m) => {
        const hw = labelWidth(m.v.label) / 2;
        const hh = LABEL_H / 2;
        const ax = m.x + ANCHOR_DX + hw;
        const ay = m.y + ANCHOR_DY - hh;
        const prev = this._labelPos.get(m.v.id);
        return {
          id: m.v.id,
          cx: prev ? prev.cx : ax,
          cy: prev ? prev.cy : ay,
          hw,
          hh,
          ax,
          ay,
        };
      });
    declutterLabels(labels, obstacles, vps);

    const seen = new Set<string>();
    for (const l of labels) {
      this._labelPos.set(l.id, { cx: l.cx, cy: l.cy });
      seen.add(l.id);
    }
    // Drop persisted positions for vehicles that are gone OR now off-screen so
    // a returning vehicle re-seeds cleanly from its anchor.
    for (const id of Array.from(this._labelPos.keys())) {
      if (!seen.has(id)) this._labelPos.delete(id);
    }
    const labelById = new Map(labels.map((l) => [l.id, l]));

    this._gHalo.replaceChildren();
    this._gLeader.replaceChildren();
    this._gMarker.replaceChildren();
    this._gLabel.replaceChildren();

    for (const m of markers) {
      const offline = m.v.status === "offline";
      const latlng = L.latLng(m.v.position);
      // undefined when the vehicle's position is off-screen (see filter above).
      const label = labelById.get(m.v.id);

      // --- Confidence halo (toggle + data gated), true-meter radius. ---
      if (this._showConfidence && m.v.confidenceRadiusM != null) {
        const r = metersToPixels(m.v.confidenceRadiusM, latlng.lat, this._zoom);
        this._gHalo.appendChild(
          svgEl("circle", {
            cx: m.x,
            cy: m.y,
            r: Math.max(2, r),
            fill: m.color,
            "fill-opacity": 0.14,
            stroke: m.color,
            "stroke-opacity": 0.5,
            "stroke-width": 1.5,
            ...(offline ? { "stroke-dasharray": "4 3" } : {}),
          }),
        );
      }

      // --- Marker dot (online solid, offline dashed ring), status color. ---
      const dot = svgEl("circle", {
        cx: m.x,
        cy: m.y,
        r: 7,
        fill: m.color,
        "fill-opacity": offline ? 0.35 : 0.6,
        stroke: m.color,
        "stroke-width": 2,
        cursor: "pointer",
        ...(offline ? { "stroke-dasharray": "3 2" } : {}),
      });
      dot.style.pointerEvents = "auto";
      dot.addEventListener("click", (ev) => {
        ev.stopPropagation();
        m.v.action();
      });
      this._gMarker.appendChild(dot);

      // Off-screen vehicles get only the marker + halo — no label, no leader.
      if (!label) continue;

      // --- Leader line: marker -> label, in the status color. ---
      this._gLeader.appendChild(
        svgEl("line", {
          x1: m.x,
          y1: m.y,
          x2: label.cx - label.hw,
          y2: label.cy,
          stroke: m.color,
          "stroke-width": 1,
          "stroke-opacity": 0.7,
        }),
      );

      // --- Label pill: white bg, status-colored bold text (matches the old
      // marker-label look); offline gets a dashed border. ---
      const lw = label.hw * 2;
      const lx = label.cx - label.hw;
      const ly = label.cy - label.hh;
      const pill = svgEl("g", {
        transform: `translate(${lx},${ly})`,
        cursor: "pointer",
      });
      pill.style.pointerEvents = "auto";
      const rect = svgEl("rect", {
        x: 0,
        y: 0,
        width: lw,
        height: LABEL_H,
        rx: 4,
        fill: "#ffffff",
        stroke: m.color,
        "stroke-width": offline ? 1 : 1.5,
        ...(offline ? { "stroke-dasharray": "4 3" } : {}),
      });
      pill.appendChild(rect);
      const text = svgEl("text", {
        x: lw / 2,
        y: LABEL_H / 2 + 0.5,
        "text-anchor": "middle",
        "dominant-baseline": "central",
        "font-family": "Inter, system-ui, sans-serif",
        "font-size": 13,
        "font-weight": offline ? 600 : 700,
        fill: m.color,
      });
      text.textContent = m.v.label;
      pill.appendChild(text);
      this._gLabel.appendChild(pill);

      // --- Hover + click. Pointing at either the dot or its label glows the
      // label (and dot) in the selection color; clicking either opens that
      // tractor's page via vehicle.action(). One shared hover so the marker
      // and its (possibly relocated) label highlight together. ---
      const setHover = (on: boolean) => {
        rect.setAttribute("stroke", on ? HOVER : m.color);
        rect.setAttribute("stroke-width", on ? "2.5" : offline ? "1" : "1.5");
        text.setAttribute("fill", on ? HOVER : m.color);
        dot.setAttribute("stroke", on ? HOVER : m.color);
        pill.style.filter = on ? `drop-shadow(0 0 5px ${HOVER})` : "";
        dot.style.filter = on ? `drop-shadow(0 0 4px ${HOVER})` : "";
      };
      // Shared hover on dot + pill (the dot's click is already wired above).
      for (const el of [dot, pill]) {
        el.addEventListener("mouseenter", () => {
          this._hoveredId = m.v.id;
          setHover(true);
        });
        el.addEventListener("mouseleave", () => {
          if (this._hoveredId === m.v.id) this._hoveredId = null;
          setHover(false);
        });
      }
      pill.addEventListener("click", (ev) => {
        ev.stopPropagation();
        m.v.action();
      });
      // Re-assert the glow if this vehicle was hovered before a redraw.
      if (this._hoveredId === m.v.id) setHover(true);
    }
  }
}
